import { Fragment } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import MuiLink from "@mui/material/Link";
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import Tooltip from "@mui/material/Tooltip";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

function SimpleBookingCard({ image, title, description, categories, action, available }) {
  return (
    <Card style={{ position: 'relative', height: '100%' }}>
      {available !== undefined && (
        <Tooltip title={available ? "Available" : "Unavailable"} arrow>
          <MKBox
            sx={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              color: available ? 'limegreen' : 'grey',
            }}
          >
            {available ? <CheckCircleIcon /> : <CancelIcon />}
          </MKBox>
        </Tooltip>
      )}
      <MKBox p={3} mt={-2} style={{ height: '100%' }}>
        {image && (
          <MKBox
            component="img"
            src={image}
            alt={title}
            width="100%"
            borderRadius="lg"
            mb={2}
          />
        )}
        {categories.length > 0 && (
          <MKTypography
            display="block"
            variant="button"
            color="text"
            fontWeight="regular"
            mb={0.75}
          >
            {categories.map((category, index) => (
              <Fragment key={index}>
                {category}
                {index < categories.length - 1 && <>&nbsp;&bull;&nbsp;</>}
              </Fragment>
            ))}
          </MKTypography>
        )}
        <MKTypography display="inline" variant="h5" fontWeight="bold">
          {title}
        </MKTypography>
        <MKBox mt={1} mb={3}>
          <MKTypography variant="body2" component="p" color="text" style={{ textAlign: 'justify' }}>
            {description}
          </MKTypography>
        </MKBox>
        {action.type === "external" ? (
          <MKButton
            component={MuiLink}
            href={action.route}
            target="_blank"
            rel="noreferrer"
            variant="outlined"
            size="small"
            color={action.color ? action.color : "dark"}
            disabled={available === false}
          >
            {action.label}
          </MKButton>
        ) : (
          <MKButton
            component={Link}
            to={action.route}
            variant="outlined"
            size="small"
            color={action.color ? action.color : "dark"}
            disabled={available === false}
          >
            {action.label}
          </MKButton>
        )}
      </MKBox>
    </Card>
  );
}

// Setting default props for the SimpleBookingCard
SimpleBookingCard.defaultProps = {
  categories: [],
  image: "", // Provide a default value for image if needed
  available: undefined, // Make available optional
};

// Typechecking props for the SimpleBookingCard
SimpleBookingCard.propTypes = {
  image: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  categories: PropTypes.arrayOf(PropTypes.string),
  action: PropTypes.shape({
    type: PropTypes.oneOf(["external", "internal"]).isRequired,
    route: PropTypes.string.isRequired,
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
    label: PropTypes.string.isRequired,
  }).isRequired,
  available: PropTypes.bool, // Make available optional
};

export default SimpleBookingCard;